import * as cartApi from '../../helpers/cartApi';
import { getTranslation } from '../../helpers';
import initCarousel from '../../helpers/carousel';
import { updateCart } from '../../helpers/updateCart';

const GWP_COOKIE = 'gwp_remove';

const GWP_SPLIDE_OPTIONS = {
  perPage: 4,
  perMove: 1,
  gap: '0.25rem',
  pagination: false,
  drag: false,
};

let syncPromise = null;

function getGwpRemoveCookie() {
  const match = document.cookie.match(/(?:^|; )gwp_remove=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function setGwpRemoveCookie() {
  document.cookie = `${GWP_COOKIE}=1; path=/; max-age=${60 * 60 * 24 * 365}`;
}

function removeGwpRemoveCookie() {
  document.cookie = `${GWP_COOKIE}=; path=/; max-age=0`;
}

function destroyGwpSplide(root) {
  if (root._gwpSplide) {
    root._gwpSplide.destroy(true);
    root._gwpSplide = null;
  }
}

function toProductId(id) {
  const n = Number(id);
  return Number.isNaN(n) ? id : n;
}

function parseVariantId(raw) {
  if (raw == null || raw === '') {
    return NaN;
  }
  const n = parseInt(String(raw), 10);
  return Number.isNaN(n) ? NaN : n;
}

/** Prefer getAttribute: `dataset` on option elements can be unreliable. */
function readGwpOptionVariantId(opt) {
  if (!opt) {
    return NaN;
  }
  const raw =
    opt.getAttribute('data-gwp-variant-id') ?? opt.dataset.gwpVariantId;
  return parseVariantId(raw);
}

/**
 * Cart line items need a variant id. Prefer Liquid/DOM (`data-gwp-variant-id`) for the
 * current selection so we never submit a stale `data-variant-id` from the add button.
 * JSON `variants[0]` is a last resort when DOM ids are missing.
 * @param {HTMLElement} root [data-cart-gwp]
 * @param {HTMLElement} ui [data-gwp-ui]
 * @param {object | undefined} selectedProduct entry from `computeState().products`
 * @returns {number}
 */
function resolveGwpVariantId(root, ui, selectedProduct) {
  const sid = root._gwpSelectedId;
  const dropdown = ui.querySelector('[data-gwp-product-dropdown]');

  if (dropdown) {
    const opt =
      [...dropdown.options].find(
        o =>
          !o.disabled &&
          sid != null &&
          toProductId(o.value) === toProductId(sid),
      ) || [...dropdown.options].find(o => !o.disabled);
    const fromOpt = readGwpOptionVariantId(opt);
    if (Number.isFinite(fromOpt) && fromOpt > 0) {
      return fromOpt;
    }
  }

  for (const btn of ui.querySelectorAll('[data-gwp-product-select]')) {
    const slide = btn.closest('.splide__slide');
    if (slide?.classList.contains('hidden')) {
      continue;
    }
    if (
      sid != null &&
      toProductId(btn.dataset.gwpProductId) === toProductId(sid)
    ) {
      const fromBtn = parseVariantId(btn.dataset.gwpVariantId);
      if (Number.isFinite(fromBtn) && fromBtn > 0) {
        return fromBtn;
      }
    }
  }

  const firstVisible = [
    ...ui.querySelectorAll('[data-gwp-product-select]'),
  ].find(btn => {
    const slide = btn.closest('.splide__slide');
    return !slide?.classList.contains('hidden');
  });
  const fromFirst = parseVariantId(firstVisible?.dataset.gwpVariantId);
  if (Number.isFinite(fromFirst) && fromFirst > 0) {
    return fromFirst;
  }

  const fromJson = parseVariantId(selectedProduct?.variants?.[0]?.id);
  if (Number.isFinite(fromJson) && fromJson > 0) {
    return fromJson;
  }

  return NaN;
}

/** Metaobject `product | json` often omits `available`; treat missing as sellable. */
function isProductAvailable(p) {
  if (p == null) {
    return false;
  }
  if (p.available === false || p.available === 'false') {
    return false;
  }
  return true;
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
function orderThresholdMajorUnits(raw) {
  if (raw == null || raw === '') {
    return NaN;
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? NaN : n;
  }
  if (typeof raw === 'object' && raw.amount != null) {
    const n = parseFloat(String(raw.amount));
    return Number.isNaN(n) ? NaN : n;
  }
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? NaN : n;
}

function giftValueToSubtractCents(cart, gwpProductIds) {
  const idSet = new Set(gwpProductIds);
  const line = cart.items.find(
    item =>
      idSet.has(toProductId(item.product_id)) &&
      item.properties &&
      item.properties.Gift,
  );
  if (!line) {
    return 0;
  }
  return line.original_line_price || 0;
}

function buildRemovalUpdates(cart, gwpProductIds) {
  const idSet = new Set(gwpProductIds);
  const updates = {};
  cart.items.forEach(item => {
    if (
      idSet.has(toProductId(item.product_id)) &&
      item.properties &&
      item.properties.Gift
    ) {
      updates[item.key] = 0;
    }
  });
  return updates;
}

function computeState(cart, data) {
  const orderThreshold = orderThresholdMajorUnits(data.orderThreshold);
  const availableProducts = (data.products || []).filter(isProductAvailable);
  const gwpProductIds = availableProducts.map(p => toProductId(p.id));

  const gwpIdSet = new Set(gwpProductIds);
  const inCartIds = new Set(
    cart.items
      .filter(item => gwpIdSet.has(toProductId(item.product_id)))
      .map(item => toProductId(item.product_id)),
  );

  let products = availableProducts.filter(
    p => !inCartIds.has(toProductId(p.id)),
  );
  if (data.automatic && availableProducts.length > 0) {
    const firstNotInCart = availableProducts.find(
      p => !inCartIds.has(toProductId(p.id)),
    );
    products = firstNotInCart ? [firstNotInCart] : [];
  }

  const hasGwp = cart.items.some(
    item =>
      gwpIdSet.has(toProductId(item.product_id)) &&
      item.properties &&
      item.properties.Gift,
  );

  return {
    data,
    orderThreshold,
    availableProducts,
    gwpProductIds,
    products,
    hasGwp,
  };
}

/**
 * Show or hide Liquid-rendered GWP UI; sync selection, carousel, and add button.
 * @param {HTMLElement} root [data-cart-gwp]
 * @param {object} cart
 * @param {ReturnType<typeof computeState>} state
 */
function syncGwpUi(root, cart, state) {
  const ui = root.querySelector('[data-gwp-ui]');
  if (!ui) {
    return;
  }

  destroyGwpSplide(root);

  const hideUi = () => {
    ui.hidden = true;
  };

  const { data, orderThreshold, products, hasGwp } = state;

  if (data.automatic || !products.length) {
    hideUi();
    return;
  }

  if (
    Number.isNaN(orderThreshold) ||
    orderThreshold <= 0 ||
    !(orderThreshold < cart.items_subtotal_price / 100) ||
    hasGwp
  ) {
    hideUi();
    return;
  }

  const gwpIdSet = new Set(products.map(p => toProductId(p.id)));

  ui.querySelectorAll('.splide__slide[data-gwp-product-id]').forEach(slide => {
    const id = toProductId(slide.dataset.gwpProductId);
    slide.classList.toggle('hidden', !gwpIdSet.has(id));
  });

  const dropdown = ui.querySelector('[data-gwp-product-dropdown]');
  if (dropdown) {
    dropdown.querySelectorAll('option[data-gwp-product-id]').forEach(opt => {
      const id = toProductId(opt.dataset.gwpProductId);
      opt.disabled = !gwpIdSet.has(id);
    });
    const sel = dropdown.selectedOptions[0];
    if (!sel || sel.disabled) {
      const first = [...dropdown.querySelectorAll('option')].find(
        o => o.dataset.gwpProductId && !o.disabled,
      );
      if (first) {
        dropdown.value = first.value;
        root._gwpSelectedId = toProductId(first.value);
      }
    }
  }

  const slideVisible = [
    ...ui.querySelectorAll('.splide__slide[data-gwp-product-id]'),
  ].filter(s => !s.classList.contains('hidden')).length;

  const optionVisible = dropdown
    ? [...dropdown.querySelectorAll('option')].filter(
        o => o.dataset.gwpProductId && !o.disabled,
      ).length
    : 0;

  const hasChoice = slideVisible > 0 || (dropdown ? optionVisible > 0 : false);
  if (!hasChoice) {
    hideUi();
    return;
  }

  ui.hidden = false;

  const validIds = new Set(products.map(p => toProductId(p.id)));
  if (!validIds.has(toProductId(root._gwpSelectedId))) {
    root._gwpSelectedId = toProductId(products[0].id);
  }

  if (dropdown) {
    dropdown.value = String(root._gwpSelectedId);
    const opt = dropdown.selectedOptions[0];
    const thumb =
      ui.querySelector('[data-gwp-dropdown-thumb-img]') ||
      ui.querySelector('[data-gwp-dropdown-thumb] img');
    const imageUrl =
      opt?.getAttribute('data-gwp-image') || opt?.dataset.gwpImage;
    const imageTitle = opt?.getAttribute('data-gwp-title') || opt?.dataset.gwpTitle;
    if (thumb && imageUrl) {
      // `image_tag` sets srcset; changing `src` alone does not update the visible image.
      const picture = thumb.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach(s => s.remove());
      }
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
      thumb.src = imageUrl;
      thumb.alt = imageTitle || '';
    }
  }

  ui.querySelectorAll('[data-gwp-product-select]').forEach(btn => {
    const slide = btn.closest('.splide__slide');
    if (slide?.classList.contains('hidden')) {
      return;
    }
    const pid = toProductId(btn.dataset.gwpProductId);
    const isSel = pid === toProductId(root._gwpSelectedId);
    btn.classList.toggle('border-primary', isSel);
    btn.classList.toggle('border-transparent', !isSel);
  });

  const colourLabel = getTranslation('products.color');

  const selectedProduct = products.find(
    p => toProductId(p.id) === toProductId(root._gwpSelectedId),
  );

  const colourEl = ui.querySelector('[data-gwp-colour-line]');
  if (colourEl && selectedProduct) {
    const part = selectedProduct.title.split('-')[1];
    colourEl.textContent = part
      ? `${colourLabel}: ${part.trim()}`
      : selectedProduct.title;
  }

  const addBtn = ui.querySelector('[data-gwp-add]');
  if (addBtn) {
    const vid = resolveGwpVariantId(root, ui, selectedProduct);
    if (Number.isFinite(vid) && vid > 0) {
      addBtn.dataset.variantId = String(vid);
    }
  }

  const host = ui.querySelector('[data-gwp-carousel-host]');
  if (host && slideVisible > 0) {
    root._gwpSplide = initCarousel(host, GWP_SPLIDE_OPTIONS, true);
  }
}

async function applyCartMutations(cart, state) {
  const { data, orderThreshold, gwpProductIds, products, hasGwp } = state;
  const originTotal =
    cart.original_total_price - giftValueToSubtractCents(cart, gwpProductIds);

  if (Number.isNaN(orderThreshold) || orderThreshold <= 0) {
    return false;
  }

  if (data.automatic) {
    if (originTotal / 100 < orderThreshold) {
      const updates = buildRemovalUpdates(cart, gwpProductIds);
      if (Object.keys(updates).length) {
        const res = await cartApi.update({ updates });
        if (!res.error && res.sections) {
          updateCart({ sections: res.sections });
        }
      }
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
      return true;
    }

    const selectedProduct = hasGwp || !products.length ? null : products[0];
    if (!hasGwp && selectedProduct && !getGwpRemoveCookie()) {
      const res = await cartApi.add({
        items: [
          {
            id: selectedProduct.variants[0].id,
            quantity: 1,
            properties: {
              Gift: 'with purchase',
              _gift_order_threshold: orderThreshold,
              _final_sale: 'true',
            },
          },
        ],
      });
      if (!res.error && res.sections) {
        updateCart({ sections: res.sections, openCart: true });
      }
      if (window.location.pathname === '/cart') {
        setTimeout(() => window.location.reload(), 500);
      }
      return true;
    }
    return false;
  }

  if (originTotal / 100 < orderThreshold) {
    const updates = buildRemovalUpdates(cart, gwpProductIds);
    if (Object.keys(updates).length) {
      const res = await cartApi.update({ updates });
      if (!res.error && res.sections) {
        updateCart({ sections: res.sections });
      }
      return true;
    }
  }

  return false;
}

export default class GiftWithPurchase {
  static instances = new Set();
  static documentListenersBound = false;

  /**
   * @param {{ getRoot: () => Element | null, config: object | null }} options
   */
  constructor({ getRoot, config }) {
    this.getRoot = getRoot;
    this.config = config;
    if (!this.config) {
      return;
    }
    GiftWithPurchase.instances.add(this);
    GiftWithPurchase.bindDocumentListenersOnce();
    this.sync();
  }

  static getConfig() {
    const withConfig = [...GiftWithPurchase.instances].filter(i => i.config);
    return withConfig[0]?.config ?? null;
  }

  static async refreshUiOnly() {
    const instances = [...GiftWithPurchase.instances].filter(i => i.config);
    if (!instances.length) {
      return;
    }
    const config = instances[0].config;
    const cart = await cartApi.get();
    const state = computeState(cart, config);
    instances.forEach(i => {
      const r = i.getRoot();
      if (r) {
        syncGwpUi(r, cart, state);
      }
    });
  }

  static bindDocumentListenersOnce() {
    if (GiftWithPurchase.documentListenersBound) {
      return;
    }
    GiftWithPurchase.documentListenersBound = true;

    document.addEventListener('click', e => {
      const pick = e.target.closest('[data-gwp-product-select]');
      if (!pick) {
        return;
      }
      const cartGwp = pick.closest('[data-cart-gwp]');
      if (!cartGwp) {
        return;
      }
      const slide = pick.closest('.splide__slide');
      if (slide?.classList.contains('hidden')) {
        return;
      }
      cartGwp._gwpSelectedId = toProductId(pick.dataset.gwpProductId);
      GiftWithPurchase.refreshUiOnly();
    });

    document.addEventListener('change', e => {
      const sel = e.target;
      if (!(sel instanceof HTMLSelectElement)) {
        return;
      }
      if (!sel.matches('[data-gwp-product-dropdown]')) {
        return;
      }
      const cartGwp = sel.closest('[data-cart-gwp]');
      if (!cartGwp) {
        return;
      }
      cartGwp._gwpSelectedId = toProductId(sel.value);
      GiftWithPurchase.refreshUiOnly();
    });

    document.addEventListener('click', e => {
      const t = e.target;
      const el = t instanceof Element ? t : t.parentElement;
      const addBtn = el?.closest('[data-gwp-add]');
      if (!addBtn) {
        return;
      }
      const cartGwp = addBtn.closest('[data-cart-gwp]');
      if (!cartGwp) {
        return;
      }
      e.preventDefault();
      const ui = cartGwp.querySelector('[data-gwp-ui]');
      const config = GiftWithPurchase.getConfig();
      if (!ui || !config) {
        return;
      }

      (async () => {
        const cart = await cartApi.get();
        const state = computeState(cart, config);
        const selectedProduct = state.products.find(
          p => toProductId(p.id) === toProductId(cartGwp._gwpSelectedId),
        );
        const variantId = resolveGwpVariantId(cartGwp, ui, selectedProduct);
        if (!Number.isFinite(variantId) || variantId <= 0) {
          return;
        }
        const threshold = orderThresholdMajorUnits(config.orderThreshold);
        const thresholdProp = Number.isNaN(threshold)
          ? config.orderThreshold
          : threshold;

        const res = await cartApi.add({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                Gift: 'with purchase',
                _gift_order_threshold: thresholdProp,
                _final_sale: 'true',
              },
            },
          ],
        });
        if (!res.error && res.sections) {
          updateCart({ sections: res.sections, openCart: true });
        }
      })();
    });

    document.addEventListener('click', e => {
      const qtyBtn = e.target.closest(
        '[data-quantity-decrement], [data-quantity-increment]',
      );
      if (!qtyBtn) {
        return;
      }
      const item = qtyBtn.closest('[data-cart-item]');
      if (item && item.hasAttribute('data-gwp-removal-opt-out')) {
        setGwpRemoveCookie();
      }
    });

    document.addEventListener('click', e => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (!removeBtn) {
        return;
      }
      const item = removeBtn.closest('[data-cart-item]');
      if (item && item.hasAttribute('data-gwp-removal-opt-out')) {
        setGwpRemoveCookie();
      }
    });
  }

  /**
   * @returns {Promise<void>}
   */
  sync() {
    return GiftWithPurchase.syncAll();
  }

  static syncAll() {
    const withConfig = [...GiftWithPurchase.instances].filter(i => i.config);
    if (!withConfig.length) {
      return Promise.resolve();
    }

    if (syncPromise) {
      return syncPromise;
    }

    const config = withConfig[0].config;

    syncPromise = (async () => {
      let cart = await cartApi.get();

      if (cart.item_count === 0) {
        removeGwpRemoveCookie();
      }

      const state = computeState(cart, config);

      if (!state.availableProducts.length) {
        withConfig.forEach(i => {
          const r = i.getRoot();
          if (r) {
            destroyGwpSplide(r);
            const ui = r.querySelector('[data-gwp-ui]');
            if (ui) {
              ui.hidden = true;
            }
          }
        });
        return;
      }

      const mutated = await applyCartMutations(cart, state);
      if (mutated) {
        return;
      }

      cart = await cartApi.get();
      const nextState = computeState(cart, config);

      withConfig.forEach(i => {
        const r = i.getRoot();
        if (r) {
          syncGwpUi(r, cart, nextState);
        }
      });
    })()
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('[GWP]', err);
      })
      .finally(() => {
        syncPromise = null;
      });

    return syncPromise;
  }
}
